import React, { useState, useEffect } from 'react';
import { BellIcon, BellSlashIcon } from '@heroicons/react/24/outline';
import { notificationAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface NotificationPreference {
  eventType: string;
  entityType: string;
  emailEnabled: boolean;
}

const EVENT_GROUPS = [
  {
    name: 'Case Events',
    entityType: 'CASE',
    events: [
      { value: 'CASE_CREATED', label: 'Case Created', description: 'When a new case is created' },
      { value: 'CASE_ASSIGNED', label: 'Case Assigned', description: 'When a case is assigned to you' },
      { value: 'CASE_REASSIGNED', label: 'Case Reassigned', description: 'When a case is reassigned' },
      { value: 'CASE_STATE_CHANGED', label: 'Case State Changed', description: 'When a case state changes' },
      { value: 'CASE_WORK_NOTE_ADDED', label: 'Work Note Added', description: 'When a work note is added to a case' },
      { value: 'CASE_COMMENT_ADDED', label: 'Comment Added', description: 'When a comment is added to a case' },
      { value: 'CASE_RESOLVED', label: 'Case Resolved', description: 'When a case is resolved' },
      { value: 'CASE_CLOSED', label: 'Case Closed', description: 'When a case is closed' },
      { value: 'CASE_CANCELLED', label: 'Case Cancelled', description: 'When a case is cancelled' },
      { value: 'CASE_REOPENED', label: 'Case Reopened', description: 'When a case is reopened' },
    ],
  },
  {
    name: 'Incident Events',
    entityType: 'INCIDENT',
    events: [
      { value: 'INCIDENT_CREATED', label: 'Incident Created', description: 'When a new incident is created' },
      { value: 'INCIDENT_ASSIGNED', label: 'Incident Assigned', description: 'When an incident is assigned to you' },
      { value: 'INCIDENT_RESOLVED', label: 'Incident Resolved', description: 'When an incident is resolved' },
      { value: 'INCIDENT_CLOSED', label: 'Incident Closed', description: 'When an incident is closed' },
    ],
  },
  {
    name: 'Problem Events',
    entityType: 'PROBLEM',
    events: [
      { value: 'PROBLEM_CREATED', label: 'Problem Created', description: 'When a new problem is created' },
      { value: 'PROBLEM_ASSIGNED', label: 'Problem Assigned', description: 'When a problem is assigned to you' },
      { value: 'PROBLEM_RESOLVED', label: 'Problem Resolved', description: 'When a problem is resolved' },
      { value: 'PROBLEM_CLOSED', label: 'Problem Closed', description: 'When a problem is closed' },
    ],
  },
  {
    name: 'Change Events',
    entityType: 'CHANGE',
    events: [
      { value: 'CHANGE_CREATED', label: 'Change Created', description: 'When a new change is created' },
      { value: 'CHANGE_APPROVED', label: 'Change Approved', description: 'When a change is approved' },
      { value: 'CHANGE_REJECTED', label: 'Change Rejected', description: 'When a change is rejected' },
      { value: 'CHANGE_CLOSED', label: 'Change Closed', description: 'When a change is closed' },
    ],
  },
  {
    name: 'Service Request Events',
    entityType: 'SERVICE_REQUEST',
    events: [
      { value: 'SERVICE_REQUEST_CREATED', label: 'Service Request Created', description: 'When a new service request is created' },
      { value: 'SERVICE_REQUEST_APPROVED', label: 'Service Request Approved', description: 'When a service request is approved' },
      { value: 'SERVICE_REQUEST_REJECTED', label: 'Service Request Rejected', description: 'When a service request is rejected' },
      { value: 'SERVICE_REQUEST_COMPLETED', label: 'Service Request Completed', description: 'When a service request is completed' },
    ],
  },
];

const UserNotificationPreferences: React.FC = () => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<NotificationPreference[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      const response = await notificationAPI.preferences.get();
      setPreferences(response.data.data || []);
    } catch (error: any) {
      showError(error.response?.data?.message || 'Failed to fetch notification preferences');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePreference = (eventType: string, entityType: string, enabled: boolean) => {
    setPreferences((prev) => {
      const existing = prev.find(p => p.eventType === eventType && p.entityType === entityType);
      if (existing) {
        return prev.map(p =>
          p.eventType === eventType && p.entityType === entityType
            ? { ...p, emailEnabled: enabled }
            : p
        );
      } else {
        return [...prev, { eventType, entityType, emailEnabled: enabled }];
      }
    });
  };

  const handleToggleAllInGroup = (group: typeof EVENT_GROUPS[0], enabled: boolean) => {
    setPreferences((prev) => {
      const newPrefs = [...prev];
      group.events.forEach(event => {
        const existing = newPrefs.find(p => p.eventType === event.value && p.entityType === group.entityType);
        if (existing) {
          existing.emailEnabled = enabled;
        } else {
          newPrefs.push({ eventType: event.value, entityType: group.entityType, emailEnabled: enabled });
        }
      });
      return newPrefs;
    });
  };

  const handleSavePreferences = async () => {
    try {
      setSaving(true);
      await notificationAPI.preferences.update({ preferences });
      showSuccess('Notification preferences updated successfully');
      fetchPreferences();
    } catch (error: any) {
      showError(error.response?.data?.message || 'Failed to save preferences');
      console.error('Error:', error);
    } finally {
      setSaving(false);
    }
  };

  const isPreferenceEnabled = (eventType: string, entityType: string): boolean => {
    const pref = preferences.find(p => p.eventType === eventType && p.entityType === entityType);
    return pref ? pref.emailEnabled : true; // Default to enabled
  };

  const areAllInGroupEnabled = (group: typeof EVENT_GROUPS[0]): boolean => {
    return group.events.every(event => isPreferenceEnabled(event.value, group.entityType));
  };

  const showSuccess = (message: string) => {
    setNotification({ message, type: 'success' });
    setTimeout(() => setNotification(null), 5000);
  };

  const showError = (message: string) => {
    setNotification({ message, type: 'error' });
    setTimeout(() => setNotification(null), 5000);
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Notification Preferences</h1>
        <p className="mt-2 text-sm text-gray-600">
          Manage your email notification preferences. You can choose which events you want to receive notifications for.
        </p>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`mb-6 p-4 rounded-md ${
          notification.type === 'success'
            ? 'bg-green-50 border border-green-200'
            : 'bg-red-50 border border-red-200'
        }`}>
          <p className={`text-sm ${
            notification.type === 'success' ? 'text-green-800' : 'text-red-800'
          }`}>
            {notification.message}
          </p>
        </div>
      )}

      {/* User Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
        <div className="flex items-center">
          <BellIcon className="h-5 w-5 text-blue-600 mr-2" />
          <div>
            <p className="text-sm font-medium text-blue-900">
              Managing preferences for: {user?.email}
            </p>
            <p className="text-sm text-blue-700">
              Changes will take effect immediately after saving.
            </p>
          </div>
        </div>
      </div>

      {/* Preferences */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>
      ) : (
        <div className="space-y-6">
          {EVENT_GROUPS.map((group) => (
            <div key={group.name} className="bg-white shadow rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-medium text-gray-900">{group.name}</h2>
                  <button
                    onClick={() => handleToggleAllInGroup(group, !areAllInGroupEnabled(group))}
                    className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-700"
                  >
                    {areAllInGroupEnabled(group) ? (
                      <>
                        <BellSlashIcon className="h-4 w-4 mr-1" />
                        Disable All
                      </>
                    ) : (
                      <>
                        <BellIcon className="h-4 w-4 mr-1" />
                        Enable All
                      </>
                    )}
                  </button>
                </div>
              </div>
              <div className="divide-y divide-gray-200">
                {group.events.map((event) => {
                  const enabled = isPreferenceEnabled(event.value, group.entityType);
                  return (
                    <div key={event.value} className="px-6 py-4 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <h3 className="text-sm font-medium text-gray-900">{event.label}</h3>
                            {enabled ? (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                Enabled
                              </span>
                            ) : (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                Disabled
                              </span>
                            )}
                          </div>
                          <p className="mt-1 text-sm text-gray-500">{event.description}</p>
                        </div>
                        <div className="ml-4">
                          <button
                            onClick={() => handleTogglePreference(event.value, group.entityType, !enabled)}
                            className={`relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                              enabled ? 'bg-primary-600' : 'bg-gray-200'
                            }`}
                          >
                            <span
                              className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200 ${
                                enabled ? 'translate-x-5' : 'translate-x-0'
                              }`}
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Save Button */}
      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSavePreferences}
          disabled={saving || loading}
          className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : 'Save Preferences'}
        </button>
      </div>
    </div>
  );
};

export default UserNotificationPreferences;
